import { createContext, useContext, useEffect, useMemo, useState, type ReactNode, useRef } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { hasPermission, isStaffUser, type Permission } from '@/features/rbac/permissions';
import { supabase, isSupabaseConfigured } from './supabaseClient';

export interface AuthContextValue {
  ready: boolean;
  configured: boolean;
  /** True when Supabase isn't configured — staff consoles render in read-only preview. */
  previewMode: boolean;
  session: Session | null;
  user: User | null;
  roles: string[];
  permissions: string[];
  isStaff: boolean;
  isOwner: boolean;
  /** Human-facing name for the signed-in user (agent name in the support console). */
  displayName: string;
  can: (permission: Permission) => boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string) => Promise<{ error?: string; needsConfirmation?: boolean }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function rolesFromUser(user: User | null): string[] {
  if (!user) return [];
  const meta = user.app_metadata as Record<string, unknown> | undefined;
  const single = typeof meta?.role === 'string' ? [meta.role as string] : [];
  const many = Array.isArray(meta?.roles) ? (meta.roles as string[]) : [];
  return [...new Set([...single, ...many])];
}

function permissionsFromUser(user: User | null): string[] {
  const meta = user?.app_metadata as Record<string, unknown> | undefined;
  return Array.isArray(meta?.permissions) ? (meta.permissions as string[]) : [];
}

function displayNameFromUser(user: User | null): string {
  const meta = user?.user_metadata as Record<string, unknown> | undefined;
  const dn = (meta?.display_name ?? meta?.name) as string | undefined;
  if (typeof dn === 'string' && dn.trim()) return dn.trim();
  const email = user?.email ?? '';
  return email ? email.split('@')[0] : '';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(!isSupabaseConfigured);
  // True once this tab has logged a sign_in (prevents duplicate rows on refires).
  const signedInLogged = useRef(false);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      // Session activity log for the admin "User Data" view. Only REAL
      // transitions are recorded: SIGNED_IN fires once per fresh login (we
      // ignore tab-focus refires via the flag) and SIGNED_OUT on logout.
      // Fire-and-forget — never blocks auth.
      try {
        if (event === 'SIGNED_IN' && next?.user && !signedInLogged.current) {
          signedInLogged.current = true;
          void supabase!.rpc('log_auth_event', { p_event: 'sign_in' });
        } else if (event === 'SIGNED_OUT') {
          signedInLogged.current = false; // sign_out row is written in signOut() pre-logout
        }
      } catch {
        /* ignore */
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const user = session?.user ?? null;
    const roles = rolesFromUser(user);
    const permissions = permissionsFromUser(user);
    return {
      ready,
      configured: isSupabaseConfigured,
      previewMode: !isSupabaseConfigured,
      session,
      user,
      roles,
      permissions,
      isOwner: roles.includes('owner'),
      isStaff: isStaffUser(roles, permissions),
      displayName: displayNameFromUser(user),
      // In preview (no Supabase) grant everything so the staff consoles are fully
      // reviewable; real permission checks apply the moment Supabase is configured.
      can: (permission) => !isSupabaseConfigured || hasPermission(roles, permissions, permission),
      async signIn(email, password) {
        if (!supabase) return { error: 'Authentication is not configured.' };
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        // Generic message — never reveal whether the account exists.
        return error ? { error: 'Invalid email or password.' } : {};
      },
      async signUp(email, password) {
        if (!supabase) return { error: 'Authentication is not configured.' };
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) return { error: 'Could not create your account. Please try again.' };
        // A sign-up may return a user WITHOUT a session (email confirmation on).
        return { needsConfirmation: !data.session };
      },
      async signOut() {
        // Log BEFORE the session is destroyed — afterwards the RPC would be
        // anonymous and the sign-out would never appear in the activity log.
        try {
          await supabase?.rpc('log_auth_event', { p_event: 'sign_out' });
        } catch {
          /* never block logout on logging */
        }
        await supabase?.auth.signOut();
      },
    };
  }, [session, ready]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
